<template>
  <div class="space-y-8">
    <div class="flex items-center justify-between">
      <div class="flex flex-wrap items-center gap-3">
        <h1 class="text-base/7 font-semibold text-gray-900 dark:text-white">Edit Runner</h1>
        <span
          v-if="isGithub"
          class="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
          data-testid="runner-platform"
        >
          {{ PLATFORM_LABELS[platform] }}
        </span>
        <span
          v-if="providerKnown"
          class="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
          data-testid="runner-provider"
        >
          {{ texts.name }}
        </span>
      </div>
    </div>

    <div
      v-if="notice"
      class="flex max-w-xl items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
      role="status"
      data-testid="edit-notice"
    >
      <ExclamationTriangleIcon class="mt-0.5 size-5 shrink-0" aria-hidden="true" />
      <p>{{ notice }}</p>
    </div>

    <form class="space-y-8" @submit.prevent="onSubmit()">
      <!-- Basic info -->
      <div class="grid grid-cols-1 gap-6 sm:grid-cols-1 max-w-xl">
        <!-- Name -->
        <div>
          <label for="name" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
            >Name</label
          >
          <div class="mt-2">
            <input
              type="text"
              name="name"
              id="name"
              v-model.trim="form.name"
              @input="touch('name')"
              :class="inputClass('name')"
              placeholder="my-runner"
              aria-describedby="name-desc"
              data-1p-ignore
            />
          </div>
          <p v-if="errors.name" class="mt-2 text-sm text-red-600 dark:text-red-400" id="name-error">
            {{ errors.name }}
          </p>
          <p v-else class="mt-2 text-sm text-gray-500 dark:text-gray-400" id="name-desc">
            Letters, numbers, spaces and dashes e.g., my new server-01
          </p>
        </div>

        <!-- Product selection -->
        <div class="sm:col-span-1">
          <ProductSelect
            v-model="form.productId"
            :products="filteredProducts"
            :loading="productsLoading"
            :select-class="selectClass('productId')"
            :aria-invalid="touched.productId && !!(errors.productId ?? '')"
            :error="errors.productId"
            :products-error="productsError"
            :show-empty-message="showNoProductsMessage"
            :empty-message="noProductsMessage"
          />

          <!-- Selected product specs preview -->
          <div
            v-if="selectedSpecs"
            class="mt-3 rounded-lg border border-gray-200 p-3 text-sm dark:border-white/10"
          >
            <dl class="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
              <div>
                <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Chip
                </dt>
                <dd class="font-medium text-gray-900 dark:text-white">{{ selectedSpecs.chip }}</dd>
              </div>
              <div>
                <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  vCPU
                </dt>
                <dd class="font-medium text-gray-900 dark:text-white">{{ selectedSpecs.vcpu }}</dd>
              </div>
              <div>
                <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  RAM
                </dt>
                <dd class="font-medium text-gray-900 dark:text-white">{{ selectedSpecs.ram }}</dd>
              </div>
              <div>
                <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Disk
                </dt>
                <dd class="font-medium text-gray-900 dark:text-white">{{ selectedSpecs.disk }}</dd>
              </div>
            </dl>
          </div>
        </div>
        <!-- Location (read-only, full width; includes price when available) -->
        <div>
          <label
            for="locationCountry"
            class="block text-sm/6 font-medium text-gray-900 dark:text-white"
            >Location</label
          >
          <div class="mt-2">
            <input
              id="locationCountry"
              type="text"
              :value="locationDisplayValue"
              :title="locationFieldTitle"
              disabled
              class="block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6 dark:bg-white/5 dark:text-gray-300 dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:disabled:bg-white/10 dark:disabled:text-gray-500 dark:disabled:outline-white/5"
            />
          </div>
        </div>

        <!-- Executor + Concurrency -->
        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div v-if="!isGithub">
            <label for="executor" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
              >Executor</label
            >
            <div class="mt-2 grid grid-cols-1">
              <select
                id="executor"
                name="executor"
                v-model="form.executor"
                :class="selectClass('executor')"
                :aria-invalid="touched.executor && !!(errors.executor ?? '')"
                aria-describedby="executor-error"
              >
                <option value="docker">docker</option>
                <option value="dind">docker-in-docker</option>
                <option value="shell">shell</option>
              </select>
            </div>
            <p
              v-if="errors.executor"
              class="mt-2 text-sm text-red-600 dark:text-red-400"
              id="executor-error"
            >
              {{ errors.executor }}
            </p>
          </div>

          <div>
            <label
              for="concurrency"
              class="block text-sm/6 font-medium text-gray-900 dark:text-white"
              >Concurrency</label
            >
            <div class="mt-2">
              <input
                type="number"
                min="1"
                :max="isGithub ? GITHUB_MAX_CONCURRENCY : undefined"
                step="1"
                name="concurrency"
                id="concurrency"
                v-model.number="form.concurrency"
                :class="inputClass('concurrency')"
                placeholder="1"
              />
            </div>
            <p
              v-if="fieldServerErrors.concurrency || errors.concurrency"
              class="mt-2 text-sm text-red-600 dark:text-red-400"
              id="concurrency-error"
            >
              {{ fieldServerErrors.concurrency || errors.concurrency }}
            </p>
          </div>
        </div>
      </div>

      <!-- GitLab Host -->
      <div v-if="!isGithub" class="sm:col-span-1 max-w-xl">
        <label for="gitlabHost" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
          >GitLab Host</label
        >
        <div class="mt-2">
          <input
            type="url"
            name="gitlabHost"
            id="gitlabHost"
            v-model.trim="form.gitlabHost"
            :class="inputClass('gitlabHost')"
            placeholder="https://gitlab.example.com"
            autocomplete="url"
            aria-describedby="gitlabHost-desc"
          />
        </div>
        <p
          v-if="errors.gitlabHost"
          class="mt-2 text-sm text-red-600 dark:text-red-400"
          id="gitlabHost-error"
        >
          {{ errors.gitlabHost }}
        </p>
        <p v-else class="mt-2 text-sm text-gray-500 dark:text-gray-400" id="gitlabHost-desc">
          Base URL of your GitLab instance.
        </p>
      </div>

      <div v-else class="max-w-xl space-y-6" data-testid="github-settings">
        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div v-for="(row, index) in githubRows" :key="row[0]">
            <label
              :for="`githubScope-${index}`"
              class="block text-sm/6 font-medium text-gray-900 dark:text-white"
              >{{ row[0] }}</label
            >
            <div class="mt-2">
              <input
                :id="`githubScope-${index}`"
                type="text"
                :value="row[1]"
                title="Fixed when the runner was created."
                disabled
                class="block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6 dark:bg-white/5 dark:text-gray-300 dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:disabled:bg-white/10 dark:disabled:text-gray-500 dark:disabled:outline-white/5"
              />
            </div>
          </div>
        </div>

        <div>
          <label for="githubToken" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
            >Replace GitHub token
            <span class="font-normal text-gray-500 dark:text-gray-400">(optional)</span></label
          >
          <div class="mt-2">
            <input
              id="githubToken"
              v-model="githubToken"
              type="password"
              name="githubToken"
              :class="inputClass('githubToken')"
              placeholder="Leave empty to keep the current token"
              autocomplete="off"
              :aria-invalid="!!githubTokenError"
              aria-describedby="githubToken-feedback githubToken-desc"
              @blur="touch('githubToken')"
              data-1p-ignore
            />
          </div>
          <p
            v-if="githubTokenError"
            id="githubToken-feedback"
            class="mt-2 text-sm text-red-600 dark:text-red-400"
          >
            {{ githubTokenError }}
          </p>
          <p
            v-if="githubTokenCleared"
            class="mt-2 text-sm text-gray-700 dark:text-gray-300"
            data-testid="github-token-cleared"
          >
            The token was cleared from this page. Enter it again to retry.
          </p>
          <p id="githubToken-desc" class="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {{ GITHUB_TOKEN_LABEL }}. {{ github ? githubTokenHint(github) : '' }}
            Replacing it does not recreate the server.
            <a
              :href="GITHUB_TOKEN_PAGE"
              target="_blank"
              rel="noopener noreferrer"
              class="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
              >Create a fine-grained token on GitHub</a
            >.
          </p>
        </div>
      </div>

      <!-- Auth and tokens -->
      <div class="grid grid-cols-1 gap-6 sm:grid-cols-1 max-w-xl">
        <!-- Runner Token -->
        <div v-if="!isGithub">
          <label for="runnerToken" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
            >GitLab Runner Token</label
          >
          <div class="mt-2">
            <input
              type="password"
              name="runnerToken"
              id="runnerToken"
              v-model.trim="form.runnerToken"
              :class="inputClass('runnerToken')"
              placeholder="********"
              title="Leave this empty to reuse the existing token."
              @blur="onRunnerTokenBlur"
              data-1p-ignore
            />
          </div>
          <p
            v-if="runnerTokenError"
            class="mt-2 text-sm text-red-600 dark:text-red-400"
            id="runnerToken-error"
          >
            {{ runnerTokenError }}
          </p>
          <p
            v-else-if="errors.runnerToken"
            class="mt-2 text-sm text-red-600 dark:text-red-400"
            id="runnerToken-error"
          >
            {{ errors.runnerToken }}
          </p>
        </div>

        <!-- Cloud provider token + SSH Keys; the field keeps its Hetzner id for every provider -->
        <div>
          <label
            for="hetznerAuthToken"
            class="block text-sm/6 font-medium text-gray-900 dark:text-white"
            >{{ texts.tokenLabel }}</label
          >
          <div class="mt-2">
            <input
              type="password"
              name="hetznerAuthToken"
              id="hetznerAuthToken"
              v-model.trim="form.hetznerAuthToken"
              :class="inputClass('hetznerAuthToken')"
              placeholder="********"
              aria-describedby="hetznerAuthToken-desc"
              title="Leave this empty to reuse the existing token."
              @blur="onHetznerTokenBlur"
              data-1p-ignore
            />
          </div>
          <p
            v-if="hetznerTokenError"
            class="mt-2 text-sm text-red-600 dark:text-red-400"
            id="hetznerAuthToken-error"
          >
            {{ hetznerTokenError }}
          </p>
          <p
            v-else-if="errors.hetznerAuthToken"
            class="mt-2 text-sm text-red-600 dark:text-red-400"
            id="hetznerAuthToken-error"
          >
            {{ errors.hetznerAuthToken }}
          </p>
          <p
            v-else
            class="mt-2 text-sm text-gray-500 dark:text-gray-400"
            id="hetznerAuthToken-desc"
          >
            {{ texts.tokenHelp }}
            <a
              v-if="provider === 'digitalocean'"
              :href="DIGITALOCEAN_TOKEN_PAGE"
              target="_blank"
              rel="noopener noreferrer"
              class="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
              >Create a token on DigitalOcean</a
            >
          </p>
          <!-- SSH keys selection -->
          <div class="mt-6">
            <SshKeyChecklist
              v-model="selectedSshKeyIds"
              :keys="sshKeys"
              :loading="sshKeysLoading"
              :error="sshKeysError"
              label="SSH Keys"
              empty-message="No SSH keys available."
            />
            <p v-if="errors.sshKeyIds" class="mt-2 text-sm text-red-600 dark:text-red-400">
              {{ errors.sshKeyIds }}
            </p>
          </div>

          <!-- Labels -->
          <div class="mt-6">
            <label
              for="labels"
              class="block text-sm/6 font-medium text-gray-900 dark:text-white"
              >Labels</label
            >
            <div class="mt-2">
              <LabelsInput
                v-model="form.labels"
                :suggestions="existingLabels"
                placeholder="Add a label..."
                id="labels"
                name="labels"
                :error="!!labelsError"
              />
              <p
                v-if="labelsError"
                class="mt-2 text-sm text-red-600 dark:text-red-400"
                id="labels-error"
              >
                {{ labelsError }}
              </p>
              <p
                v-if="labelsWarning"
                class="mt-2 text-sm text-yellow-700 dark:text-yellow-400"
                data-testid="labels-warning"
              >
                {{ labelsWarning }}
              </p>
              <p v-else class="mt-2 text-sm text-gray-500 dark:text-gray-400" id="labels-desc">
                Hit Enter to add dynamically. Labels already used on your other runners are
                suggested.
                <template v-if="provider === 'digitalocean'">{{
                  DIGITALOCEAN_LABELS_NOTE
                }}</template>
                <template v-else
                  >Labels will be attached to the Hetzner VM. You can use them to automatically
                  apply Hetzner firewalls.</template
                >
                <template v-if="isGithub">GitHub also registers them as runner labels.</template>
              </p>
            </div>
          </div>

        </div>
      </div>

      <div v-if="!isGithub" class="max-w-xl">
        <CacheSelect v-model="cacheId" :warning="cacheWarning" />
      </div>

      <fieldset class="max-w-xl" aria-describedby="resource-metrics-desc">
        <legend class="text-sm/6 font-semibold text-gray-900 dark:text-white">Metrics</legend>
        <p id="resource-metrics-desc" class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          A lightweight collector on the VM measures CPU, RAM, swap and disk usage and, on GitLab
          runners, the running jobs every minute.
        </p>
        <div class="mt-4 space-y-6">
          <RunnerToggle
            id="monitoringEnabled"
            v-model="monitoringEnabled"
            label="Show in dashboard"
            description="Charts on the runner card."
            :disabled="!canChangeMonitoring && !monitoringEnabled"
            :warning="monitoringWarning"
          />
          <MetricsSelect v-model="metricsConnectionId" :warning="metricsWarning" />
          <RunnerWatchdogFields
            v-model:enabled="watchdog.enabled"
            v-model:timeout-minutes="watchdog.timeoutMinutes"
            :available="watchdogIsAvailable"
            :error="watchdogServerError"
            :note="watchdogNote"
          />
        </div>
      </fieldset>

      <!-- Actions / Submit -->
      <div class="flex flex-wrap items-center justify-start gap-3 max-w-xl">
        <button
          type="button"
          @click="router.push({ name: 'Dashboard' })"
          class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:border-white/10 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <!--        <button-->
        <!--          type="button"-->
        <!--          @click="onDuplicate"-->
        <!--          :disabled="submitting"-->
        <!--          class="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:border-white/10 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"-->
        <!--        >-->
        <!--          <DocumentDuplicateIcon class="size-5 text-gray-400" aria-hidden="true" />-->
        <!--          Duplicate-->
        <!--        </button>-->
        <button
          type="submit"
          class="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="submitting || planBlocked"
        >
          <span v-if="!submitting">Save changes</span>
          <span v-else>Saving…</span>
        </button>
      </div>
      <div class="mt-4">
        <PlanRequiredNotice v-if="planBlocked" :recheck="recheckPlan" />
        <p v-else-if="serverError" class="text-sm text-red-600 dark:text-red-400">
          {{ serverError }}
        </p>
        <p v-if="successMessage" class="text-sm text-green-700 dark:text-green-400">
          {{ successMessage }}
        </p>
      </div>
    </form>

    <ConfirmDialog
      :open="confirmingRelabel"
      :title="relabelPausesNow ? 'Pause the runner now?' : 'Recreate the runner\'s VM?'"
      :message="
        relabelPausesNow
          ? 'GitHub stores the labels with the runner\'s registration, so saving new labels of a pausing runner deletes its VM now and leaves it paused. Jobs running on it will be interrupted.'
          : 'GitHub stores the labels with the runner\'s registration, so saving new labels recreates the VM. Jobs running on it will be interrupted.'
      "
      :confirm-label="relabelPausesNow ? 'Save and pause now' : 'Save and recreate'"
      busy-label="Saving…"
      @confirm="confirmRelabel"
      @cancel="confirmingRelabel = false"
    />
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, reactive, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {listProducts, type Product, type ProductDatacenter} from '@/api/products';
import type {SshKeyDto} from '@/api/hetzner';
import {
  getRunner,
  getRunnerAvailableSshKeys,
  type GithubRunnerScope,
  listExistingRunnerLabels,
  runnerProblem,
  type RunnerPlatform,
  updateRunner,
  type UpdateRunnerRequest,
} from '@/api/runners';
import type {MonthlyPrice} from '@/api/types';
import ProductSelect from '@/components/ProductSelect.vue';
import LabelsInput from '@/components/LabelsInput.vue';
import SshKeyChecklist from '@/components/SshKeyChecklist.vue';
import RunnerToggle from '@/components/RunnerToggle.vue';
import CacheSelect from '@/components/CacheSelect.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import { ExclamationTriangleIcon } from '@heroicons/vue/20/solid';
import { useMonitoringEntitlement } from '@/lib/monitoring-entitlement';
import MetricsSelect from '@/components/MetricsSelect.vue';
import RunnerWatchdogFields from '@/components/RunnerWatchdogFields.vue';
import {getCountryInfo, normalizeCountryCode} from '@/lib/country';
import {formatMonthlyNetPrice, monthlyNetPriceTitle} from '@/lib/format';
import {validateRunnerName} from '@/lib/validation';
import {
  validateWatchdogTimeout,
  watchdogAvailable,
  watchdogFormOf,
  type WatchdogForm,
  WATCHDOG_LIMIT_FORM_NOTE,
  watchdogUpdateFields,
} from '@/lib/watchdog';
import PlanRequiredNotice from '@/components/PlanRequiredNotice.vue';
import { isSubscriptionRequired } from '@/lib/subscription-required';
import { usePlanRequired } from '@/lib/entitlement';
import {
  GITHUB_MAX_CONCURRENCY,
  GITHUB_TOKEN_PAGE,
  PLATFORM_LABELS,
  runnerPlatform,
  validateGithubConcurrency,
  validateGithubLabels,
  validateGithubToken,
} from '@/lib/platforms';
import { GITHUB_TOKEN_LABEL, githubScopeRows, githubTokenHint } from '@/lib/mcp-handoffs';
import {
  cloudProvider,
  type CloudProvider,
  DIGITALOCEAN_LABELS_NOTE,
  DIGITALOCEAN_TOKEN_PAGE,
  listProviderSshKeys,
  PROVIDER_TEXTS,
  productsOf,
} from '@/lib/providers';

const route = useRoute();
const router = useRouter();
const runnerId = computed(() => String(route.params.id ?? ''));

type EditRunnerForm = Omit<
  UpdateRunnerRequest,
  | 'sshKeyIds'
  | 'monitoringEnabled'
  | 'metricsConnectionId'
  | 'cacheId'
  | 'github'
  | 'watchdogEnabled'
  | 'watchdogTimeoutMinutes'
  // The token field holds the token of the runner's provider; see onSubmit.
  | 'providerAuthToken'
>;

const form = reactive<EditRunnerForm>({
  name: '',
  productId: '',
  gitlabHost: 'https://gitlab.com/',
  runnerToken: '',
  hetznerAuthToken: '',
  concurrency: 1,
  executor: 'docker',
  labels: [],
});

const touched = reactive<Record<keyof EditRunnerForm, boolean>>({
  name: false,
  productId: false,
  gitlabHost: false,
  runnerToken: false,
  hetznerAuthToken: false,
  concurrency: false,
  executor: false,
  labels: false,
});

type FieldKey = keyof UpdateRunnerRequest | 'githubToken';

interface RequestError {
  message?: string;
  response?: { data?: { detail?: string; message?: string } };
}

const errors = reactive<Partial<Record<FieldKey, string>>>({});
const fieldServerErrors = reactive<Partial<Record<FieldKey, string>>>({});
const notice = ref('');

const platform = ref<RunnerPlatform>('GITLAB');
const isGithub = computed(() => platform.value === 'GITHUB');
const github = ref<GithubRunnerScope | null>(null);
const githubRows = computed(() => (github.value ? githubScopeRows(github.value) : []));
const githubToken = ref('');
const githubTokenTouched = ref(false);
const githubTokenCleared = ref(false);
const githubTokenError = computed(
  () => fieldServerErrors.githubToken || (githubTokenTouched.value ? errors.githubToken : '') || ''
);
const labelsError = computed(() => fieldServerErrors.labels || errors.labels || '');
const originalLabels = ref<string[]>([]);
const labelsChanged = computed(
  () =>
    form.labels.length !== originalLabels.value.length ||
    form.labels.some((label, index) => label !== originalLabels.value[index])
);
// Paused runners only store the settings; a pausing one is paused at once and stays paused.
const recreateWarning = computed(() => {
  const state = runnerState.value.toUpperCase();
  if (state === 'PAUSED') return '';
  return state === 'PAUSING'
    ? 'Saving this change pauses the runner at once. Jobs running on it will be interrupted.'
    : "Saving this change recreates the runner's VM. Jobs running on it will be interrupted.";
});
// GitHub stores the labels with the runner's registration, so changing them recreates the VM.
const labelsWarning = computed(() =>
  isGithub.value && labelsChanged.value ? recreateWarning.value : ''
);
const submitting = ref(false);
const serverError = ref('');
const { planRequired, refresh: refreshPlan } = usePlanRequired();
// Set by a refusal for a missing plan; only a successful reload of the plan clears it.
const refused = ref(false);
const planBlocked = computed(() => refused.value || planRequired.value);
async function recheckPlan() {
  if (await refreshPlan()) refused.value = false;
}
const successMessage = ref('');
// The token whose SSH keys are listed and checked; an empty token is the stored one.
const lastValidatedToken = ref<string | null>(null);
let tokenCheck: { token: string; done: Promise<void> } | null = null;
// SSH keys available for this runner (from backend endpoint)
const sshKeys = ref<SshKeyDto[]>([]);
const sshKeysLoading = ref(false);
const sshKeysError = ref('');
const selectedSshKeyIds = ref<string[]>([]);

const monitoringEnabled = ref(false);
const originalMonitoringEnabled = ref(false);
const runnerState = ref('');
const { canChange: canChangeMonitoring } = useMonitoringEntitlement();
const monitoringChanged = computed(
  () => monitoringEnabled.value !== originalMonitoringEnabled.value
);
const monitoringWarning = computed(() =>
  monitoringChanged.value ? recreateWarning.value : ''
);

const metricsConnectionId = ref('');
const originalMetricsConnectionId = ref('');
const metricsChanged = computed(
  () => metricsConnectionId.value !== originalMetricsConnectionId.value
);
const metricsWarning = computed(() =>
  metricsChanged.value ? recreateWarning.value : ''
);

// Watchdog changes never recreate the VM, so they have no recreate warning.
const watchdog = reactive<WatchdogForm>(watchdogFormOf(null));
const originalWatchdog = ref<WatchdogForm>(watchdogFormOf(null));
const watchdogNote = ref('');
const watchdogServerError = ref('');
const watchdogIsAvailable = computed(() =>
  watchdogAvailable(monitoringEnabled.value, metricsConnectionId.value)
);
watch(watchdogIsAvailable, (available) => {
  if (!available) watchdog.enabled = false;
});
watch(
  () => [watchdog.enabled, watchdog.timeoutMinutes, watchdogIsAvailable.value],
  () => (watchdogServerError.value = '')
);

const cacheId = ref('');
const originalCacheId = ref('');
const cacheChanged = computed(() => cacheId.value !== originalCacheId.value);
const cacheWarning = computed(() =>
  cacheChanged.value ? recreateWarning.value : ''
);

// Server-provided error messages for tokens
const hetznerTokenError = ref('');
const runnerTokenError = ref('');

type Executor = 'docker' | 'dind' | 'shell';

// Normalize executor values coming from backend to match UI select values
function normalizeExecutor(exec: string | null | undefined): Executor {
  const v = (exec || '').toLowerCase().trim();
  if (v === 'docker-in-docker' || v === 'docker_in_docker' || v === 'dind') return 'dind';
  if (v === 'shell') return 'shell';
  return 'docker';
}

// Products state
const products = ref<Product[]>([]);
const productsLoading = ref(false);
const productsError = ref<string>('');
const selectedProduct = computed(() => products.value.find((p) => p.productId === form.productId));
// A runner keeps its provider, so only that provider's products are offered and its token is asked for.
const provider = ref<CloudProvider>('hetzner');
const providerKnown = ref(false);
const texts = computed(() => PROVIDER_TEXTS[provider.value]);

const initializingRunnerData = ref(true);

// Price returned by getRunner() for the originally loaded product. Used as a fallback when
// products fail to load or the current product is no longer present/bookable in the catalog.
const originalProductId = ref<string>('');
const originalMonthlyPrice = ref<MonthlyPrice | null>(null);

// Labels used on other runners, offered as suggestions in the labels field
const existingLabels = ref<string[]>([]);

// Product specs to preview beneath selector
const selectedSpecs = computed(() => {
  const p = selectedProduct.value;
  if (!p) return null;
  return {
    chip: p.cpuArchitecture?.value ?? '—',
    vcpu: p.cpus ?? '—',
    ram: `${p.memory} GB`,
    disk: `${p.disk} GB`,
  };
});

watch(
  () => form.productId,
  () => {
    if (initializingRunnerData.value) return;

    // Pre-fill concurrency based on selected product: 2 x vCPUs
    const p = selectedProduct.value;
    if (p && p.cpus) {
      const vcpus = Number(p.cpus);
      if (!isNaN(vcpus) && vcpus > 0) {
        form.concurrency = isGithub.value ? Math.min(vcpus * 2, GITHUB_MAX_CONCURRENCY) : vcpus * 2;
      }
    } else {
      // Fallback default
      form.concurrency = 1;
    }
  }
);

function validate(field?: FieldKey): boolean {
  serverError.value = '';
  if (field) {
    delete errors[field];
  } else {
    (Object.keys(errors) as FieldKey[]).forEach((k) => delete errors[k]);
  }
  const gitlab = !isGithub.value;

  if (!gitlab && (!field || field === 'githubToken')) {
    const tokenError = validateGithubToken(githubToken.value);
    if (tokenError) errors.githubToken = tokenError;
  }
  if (!gitlab && (!field || field === 'labels')) {
    const labelsInvalid = validateGithubLabels(form.labels);
    if (labelsInvalid) errors.labels = labelsInvalid;
  }

  if (!field || field === 'name') {
    const nameError = validateRunnerName(form.name);
    if (nameError) errors.name = nameError;
  }
  if (!field || field === 'productId') {
    if (!form.productId) errors.productId = 'Product is required';
  }
  if (gitlab && (!field || field === 'gitlabHost')) {
    if (!form.gitlabHost) errors.gitlabHost = 'GitLab host is required';
    else if (!/^https?:\/\//.test(form.gitlabHost))
      errors.gitlabHost = 'Enter a valid URL (starting with http or https)';
  }
  if (field === 'runnerToken') {
    // Optional, but if we wanted custom validation on it, we'd put it here
  }
  if (gitlab && (!field || field === 'executor')) {
    if (!form.executor) errors.executor = 'Executor is required';
  }
  if (!field || field === 'concurrency') {
    if (!gitlab) {
      const concurrencyError = validateGithubConcurrency(form.concurrency);
      if (concurrencyError) errors.concurrency = concurrencyError;
    } else if (!form.concurrency || form.concurrency < 1)
      errors.concurrency = 'Concurrency must be at least 1';
  }
  return Object.keys(errors).length === 0;
}

function touch(field: keyof EditRunnerForm | 'githubToken') {
  if (field === 'githubToken') githubTokenTouched.value = true;
  else touched[field] = true;
  validate(field);
}

function inputClass(field: keyof EditRunnerForm | 'githubToken') {
  if (field === 'githubToken') {
    const base = `block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`;
    return base + (githubTokenError.value ? ' ring-red-500 focus:ring-red-500' : '');
  }
  const base = `block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`;
  const extraError =
    field === 'hetznerAuthToken'
      ? hetznerTokenError.value
      : field === 'runnerToken'
        ? runnerTokenError.value
        : '';
  const showError = fieldServerErrors[field] || (touched[field] && (errors[field] || extraError));
  return base + (showError ? ' ring-red-500 focus:ring-red-500' : '');
}
function selectClass(field: keyof EditRunnerForm) {
  return (
    `block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6` +
    (touched[field] && errors[field] ? ' ring-red-500 focus:ring-red-500' : '')
  );
}
async function onRunnerTokenBlur() {
  touched.runnerToken = true;
  validate('runnerToken');
}
function currentToken(): string {
  return form.hetznerAuthToken?.trim() || '';
}

/** Lists the SSH keys of the entered token, or the stored one, once per token. */
function onHetznerTokenBlur(): Promise<void> {
  const token = currentToken();
  if (lastValidatedToken.value === token) return Promise.resolve();
  if (tokenCheck?.token !== token) tokenCheck = { token, done: checkToken(token) };
  return tokenCheck.done;
}

async function checkToken(token: string) {
  touched.hetznerAuthToken = true;
  try {
    if (!token) {
      await loadAvailableSshKeys();
      if (token === currentToken()) lastValidatedToken.value = token;
      return;
    }
    sshKeysLoading.value = true;
    hetznerTokenError.value = '';
    try {
      const data = await listProviderSshKeys(provider.value, token);
      if (token !== currentToken()) return;
      sshKeys.value = Array.isArray(data) ? data : [];
      const validIds = new Set(sshKeys.value.map((k) => String(k.id)));
      selectedSshKeyIds.value = selectedSshKeyIds.value.filter((id) => validIds.has(id));
      lastValidatedToken.value = token;
    } catch (failure) {
      if (token !== currentToken()) return;
      const e = failure as RequestError | null;
      sshKeys.value = [];
      hetznerTokenError.value = e?.response?.data?.detail || 'Invalid Token';
      lastValidatedToken.value = token;
    } finally {
      if (token === currentToken()) sshKeysLoading.value = false;
    }
  } finally {
    if (tokenCheck?.token === token) tokenCheck = null;
  }
}

// A lookup still in flight, and the cached outcome, belong to the previous token.
watch(currentToken, () => {
  lastValidatedToken.value = null;
  hetznerTokenError.value = '';
  sshKeysLoading.value = false;
});
watch(
  () => form.runnerToken,
  () => {
    runnerTokenError.value = '';
  }
);
// Clearing the token after a request must not hide the refusal that came back for it.
watch(githubToken, (token) => {
  if (!token) return;
  delete fieldServerErrors.githubToken;
  githubTokenCleared.value = false;
});
watch(
  () => form.concurrency,
  () => delete fieldServerErrors.concurrency
);
watch(
  () => form.labels,
  () => {
    delete fieldServerErrors.labels;
    if (isGithub.value) validate('labels');
  },
  { deep: true }
);
watch(
  () => form.name,
  (newValue) => {
    // Preserve spaces in names while removing unsupported characters.
    const invalidCharsRegex = /[^a-zA-Z0-9 -]/g;
    const sanitizedValue = newValue.replace(invalidCharsRegex, '');
    if (sanitizedValue !== newValue) {
      form.name = sanitizedValue;
    }
  }
);
// Lists the keys of the stored token; an entered token replaces them.
async function loadAvailableSshKeys() {
  sshKeysError.value = '';
  try {
    sshKeysLoading.value = true;
    const keys = await getRunnerAvailableSshKeys(runnerId.value);
    if (!currentToken()) sshKeys.value = keys;
  } catch (failure) {
    if (currentToken()) return;
    const e = failure as RequestError | null;
    sshKeysError.value = e?.response?.data?.message || e?.message || 'Failed to load SSH keys';
  } finally {
    if (!currentToken()) sshKeysLoading.value = false;
  }
}

const confirmingRelabel = ref(false);
const relabelPausesNow = computed(() => runnerState.value.toUpperCase() === 'PAUSING');

function confirmRelabel() {
  confirmingRelabel.value = false;
  void onSubmit(true);
}

async function onSubmit(relabelConfirmed = false) {
  if (planBlocked.value) return;
  Object.keys(touched).forEach((key) => (touched[key as keyof EditRunnerForm] = true));
  githubTokenTouched.value = true;
  await onHetznerTokenBlur();

  if (hetznerTokenError.value || runnerTokenError.value) {
    return;
  }
  if (!validate()) return;
  if (watchdog.enabled && validateWatchdogTimeout(watchdog.timeoutMinutes)) return;
  if (labelsWarning.value && !relabelConfirmed) {
    confirmingRelabel.value = true;
    return;
  }
  submitting.value = true;
  notice.value = '';
  for (const key of Object.keys(fieldServerErrors) as FieldKey[]) delete fieldServerErrors[key];
  watchdogServerError.value = '';
  const sentToGithub = isGithub.value;
  try {
    const payload: UpdateRunnerRequest = sentToGithub
      ? {
          name: form.name,
          productId: form.productId,
          concurrency: form.concurrency,
          sshKeyIds: selectedSshKeyIds.value,
          labels: form.labels,
        }
      : {
          name: form.name,
          productId: form.productId,
          gitlabHost: form.gitlabHost,
          concurrency: form.concurrency,
          executor: form.executor,
          sshKeyIds: selectedSshKeyIds.value,
          labels: form.labels,
        };
    // Only include tokens if user provided new values; otherwise omit so backend keeps existing values
    if (!sentToGithub && form.runnerToken && form.runnerToken.trim() !== '') {
      payload.runnerToken = form.runnerToken.trim();
    }
    if (sentToGithub && githubToken.value.trim() !== '') {
      payload.github = { token: githubToken.value.trim() };
      githubToken.value = '';
      githubTokenTouched.value = false;
      githubTokenCleared.value = true;
    }
    if (form.hetznerAuthToken && form.hetznerAuthToken.trim() !== '') {
      if (provider.value === 'hetzner') payload.hetznerAuthToken = form.hetznerAuthToken.trim();
      else payload.providerAuthToken = form.hetznerAuthToken.trim();
    }
    // Omission keeps the stored preference; resending an unchanged true can still reprovision the VM.
    if (monitoringChanged.value && (canChangeMonitoring.value || !monitoringEnabled.value)) {
      payload.monitoringEnabled = monitoringEnabled.value;
    }
    // Omission keeps the current connection; an empty string switches Metrics off.
    if (metricsChanged.value) payload.metricsConnectionId = metricsConnectionId.value;
    // Omission keeps the current cache; an empty string removes it.
    if (cacheChanged.value && !sentToGithub) payload.cacheId = cacheId.value;
    Object.assign(payload, watchdogUpdateFields(watchdog, originalWatchdog.value));
    await updateRunner(runnerId.value, payload);
    githubTokenCleared.value = false;
    // Navigate back with a success flag to show notification
    await router.push({ name: 'Dashboard', query: { edited: '1' } });
  } catch (e) {
    if (isSubscriptionRequired(e)) {
      refused.value = true;
      void recheckPlan();
    }
    const problem = runnerProblem(
      e,
      (e as RequestError | null)?.message || 'Failed to update runner'
    );
    if (problem.status === 409 && problem.code === 'RUNNER_STATE_CONFLICT') {
      notice.value =
        'The runner changed while you were editing it, so nothing was saved. The form now shows its ' +
        'current settings; review your changes and save again.';
      await reloadRunner();
    } else if (problem.status === 409 && problem.code === 'RUNNER_PAUSED_TOKEN_REFUSED') {
      notice.value = problem.message;
      await reloadRunner();
    } else {
      const field = sentToGithub ? githubErrorField(problem.code) : undefined;
      if (field) fieldServerErrors[field] = problem.message;
      if (problem.code === 'INVALID_WATCHDOG') watchdogServerError.value = problem.message;
      serverError.value =
        field || watchdogServerError.value ? 'Check the highlighted field above.' : problem.message;
    }
  } finally {
    submitting.value = false;
  }
}

function githubErrorField(code: string | undefined): FieldKey | undefined {
  switch (code) {
    case 'GITHUB_TOKEN_INVALID':
    case 'GITHUB_PERMISSION_DENIED':
      return 'githubToken';
    case 'INVALID_CONCURRENCY':
      return 'concurrency';
    case 'INVALID_LABELS':
      return 'labels';
    default:
      return undefined;
  }
}

/** Shows the stored settings again without the product change resetting the concurrency. */
async function reloadRunner() {
  initializingRunnerData.value = true;
  await loadRunner();
  initializingRunnerData.value = false;
}

// async function onDuplicate() {
//   if (!runnerId.value) return;
//   submitting.value = true;
//   serverError.value = '';
//   try {
//     await duplicateRunner(runnerId.value);
//     // After duplication, navigate to Dashboard to see the new runner
//     await router.push({ name: 'Dashboard', query: { registered: '0' } });
//   } catch (e: any) {
//     serverError.value = e?.response?.data?.message || e?.message || 'Failed to duplicate runner';
//   } finally {
//     submitting.value = false;
//   }
// }

async function loadProducts() {
  productsLoading.value = true;
  productsError.value = '';
  try {
    const res = await listProducts();
    products.value = res || [];
  } catch (failure) {
    const e = failure as RequestError | null;
    productsError.value = e?.response?.data?.message || e?.message || 'Failed to load products';
  } finally {
    productsLoading.value = false;
  }
}

async function loadRunner() {
  try {
    const r = await getRunner(runnerId.value);
    platform.value = runnerPlatform(r);
    provider.value = cloudProvider(r.provider);
    providerKnown.value = true;
    github.value = r.github ?? null;
    form.name = r.name;
    // Use productId from RunnerResponse; providerProductId does not exist on RunnerResponse
    form.productId = r.productId || '';
    originalProductId.value = form.productId;
    originalMonthlyPrice.value = r.monthlyPrice ?? null;
    form.gitlabHost = r.gitlabHost || 'https://gitlab.com/';
    // Preselect with existing executor from backend
    form.executor = normalizeExecutor(r.executor);
    form.concurrency = r.concurrency ?? Math.max(1, (r.cpus ?? 1) * 2);
    locationCountry.value = r.locationCountry || '';
    locationCity.value = r.locationCity || '';
    // Preselect SSH keys already configured on the runner
    const existing: (string | number)[] | undefined = r.sshKeyIds;
    selectedSshKeyIds.value = Array.isArray(existing) ? existing.map((v) => String(v)) : [];
    form.labels = Array.isArray(r.labels) ? [...r.labels] : [];
    originalLabels.value = [...form.labels];
    runnerState.value = r.state || '';
    originalMonitoringEnabled.value = r.monitoringEnabled === true;
    monitoringEnabled.value = originalMonitoringEnabled.value;
    originalMetricsConnectionId.value = r.metricsConnectionId ?? '';
    metricsConnectionId.value = originalMetricsConnectionId.value;
    originalCacheId.value = r.cacheId ?? '';
    cacheId.value = originalCacheId.value;
    originalWatchdog.value = watchdogFormOf(r.watchdog);
    Object.assign(watchdog, originalWatchdog.value);
    watchdogNote.value = r.watchdog?.disabledReason === 'RESTART_LIMIT' ? WATCHDOG_LIMIT_FORM_NOTE : '';
  } catch (failure) {
    const e = failure as RequestError | null;
    serverError.value = e?.response?.data?.message || e?.message || 'Failed to load runner details';
  }
}

// Labels from other runners are only a convenience; a failure must not block editing.
async function loadExistingLabels() {
  try {
    existingLabels.value = await listExistingRunnerLabels(runnerId.value);
  } catch {
    existingLabels.value = [];
  }
}

onMounted(async () => {
  // Load runner first so we know which sshKeyIds to preselect
  await loadRunner();
  initializingRunnerData.value = false;
  // Then load products, available SSH keys and label suggestions
  await Promise.all([loadProducts(), loadAvailableSshKeys(), loadExistingLabels()]);
});

// Location country and filtered products
const locationCountry = ref<string>('');
const locationCity = ref<string>('');

function normalizeCity(value: string) {
  return value.trim().toLowerCase();
}

function productAvailableInDatacenter(product: Product, country: string, city: string): boolean {
  const availableDatacenters = product.datacenters ?? [];
  if (availableDatacenters.length === 0) return false;
  return availableDatacenters.some((dc) => {
    const dcCountry = normalizeCountryCode(dc?.country ?? '');
    const dcCity = normalizeCity(String(dc?.city ?? ''));
    const countryMatch = country ? dcCountry === country : false;
    const cityMatch = city ? dcCity === city : true;
    return countryMatch && cityMatch;
  });
}

const filteredProducts = computed(() => {
  const country = normalizeCountryCode(locationCountry.value);
  const city = normalizeCity(locationCity.value);
  const offered = productsOf(products.value, provider.value);
  if (!country && !city) return offered;
  return offered.filter((product) => productAvailableInDatacenter(product, country, city));
});
const showNoProductsMessage = computed(
  () => !productsLoading.value && filteredProducts.value.length === 0 && !productsError.value
);
const countryInfo = computed(() => getCountryInfo(locationCountry.value));
const locationLabel = computed(() => {
  const countryName = countryInfo.value.name || '—';
  const city = locationCity.value.trim();
  return city ? `${countryName} (${city})` : countryName;
});

function monthlyPriceForLocation(product: Product | undefined, country: string, city: string): MonthlyPrice | null {
  if (!product) return null;
  const match = (dc: ProductDatacenter | null | undefined) => {
    const dcCountry = normalizeCountryCode(dc?.country ?? '');
    const dcCity = normalizeCity(String(dc?.city ?? ''));
    const countryMatch = country ? dcCountry === country : false;
    const cityMatch = city ? dcCity === city : true;
    return countryMatch && cityMatch;
  };
  const available = (product.datacenters ?? []).find(match);
  if (available?.monthlyPrice) return available.monthlyPrice;
  const supported = (product.supportedDatacenters ?? []).find(match);
  return supported?.monthlyPrice ?? null;
}

const locationMonthlyPriceFormatted = computed(() => {
  const country = normalizeCountryCode(locationCountry.value);
  const city = normalizeCity(locationCity.value);
  const fromProduct = formatMonthlyNetPrice(
    monthlyPriceForLocation(selectedProduct.value, country, city)
  );
  // While the originally loaded product remains selected, prefer getRunner()'s monthlyPrice
  // as the authoritative source for this runner. After a product change, use catalog only.
  if (
    form.productId &&
    form.productId === originalProductId.value &&
    originalMonthlyPrice.value
  ) {
    return formatMonthlyNetPrice(originalMonthlyPrice.value);
  }
  return fromProduct;
});

const locationDisplayValue = computed(() => {
  const base = `${countryInfo.value.flag} ${locationLabel.value}`;
  const price = locationMonthlyPriceFormatted.value;
  return price ? `${base} · ${price}` : base;
});

const locationFieldTitle = computed(() => {
  const immutableNote =
    "Location cannot be changed for existing runners, because this would change the runner's IP.";
  const price = locationMonthlyPriceFormatted.value;
  return price
    ? `${immutableNote} ${monthlyNetPriceTitle(price, texts.value.priceSource)}`
    : immutableNote;
});

const noProductsMessage = computed(() => `No products available for ${locationLabel.value}.`);
</script>
